import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { Button } from "@/components/flexkit/Button";
import { NotAuthorized } from "@/components/NotAuthorized";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { toast } from "@/components/podkit/toasts/use-toast";
import { Heading3, Subheading } from "@/components/podkit/typography/Headings";
import { Switch, type SwitchState } from "@/components/Switch";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { KNOWN_EXPERIMENTS, useExperiment, type ExperimentName } from "@/hooks/use-experiment";
import { useOrganization } from "@/queries/organization-queries";
import { TrackLocations, useSegmentTrack } from "@/hooks/use-segment";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { FlaskConical } from "lucide-react";
import { useState, type FC } from "react";

type ExperimentsSettingsPageProps = {
    hideDescription?: boolean;
};

export const ExperimentsSettingsPage: FC<ExperimentsSettingsPageProps> = ({ hideDescription }) => {
    useDocumentTitle("Experiments");

    const { data: organization, isLoading: isLoadingOrganization } = useOrganization();

    if (isLoadingOrganization) {
        return <LoadingState />;
    }

    // Enterprise users shouldn't see this page (route should be hidden, but just in case)
    if (organization?.tier === OrganizationTier.ENTERPRISE) {
        return <NotAuthorized />;
    }

    return (
        <div className="flex flex-col gap-8" data-testid="experiments-settings-page">
            {!hideDescription && (
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <FlaskConical className="size-5 text-content-secondary" />
                        <Heading3>Experiments</Heading3>
                    </div>
                    <Subheading>
                        Enable experimental features to try out new functionality before it&apos;s generally available.
                        These features may change or be removed at any time.
                    </Subheading>
                </div>
            )}

            <div className="flex flex-col gap-4">
                {Object.keys(KNOWN_EXPERIMENTS).map((name) => (
                    <ExperimentCard key={name} experimentName={name as ExperimentName} />
                ))}
            </div>
        </div>
    );
};

type ExperimentCardProps = {
    experimentName: ExperimentName;
};

const ExperimentCard: FC<ExperimentCardProps> = ({ experimentName }) => {
    const { isEnabled, setEnabled, experiment } = useExperiment(experimentName);
    const [showDisableDialog, setShowDisableDialog] = useState(false);
    const segmentTrack = useSegmentTrack();

    const handleToggle = (checked: boolean) => {
        if (!checked && isEnabled) {
            // Show confirmation dialog before disabling
            setShowDisableDialog(true);
            return;
        }

        setEnabled(checked);

        if (checked) {
            segmentTrack("experiment_enabled", {
                experiment_name: experimentName,
                experiment_display_name: experiment.name,
                source: "settings",
            });

            if (experimentName === "ralph") {
                toast({
                    title: (
                        <span className="flex items-center gap-2">
                            <IconRalphWiggum size={24} />
                            <span>Ralph Mode enabled!</span>
                        </span>
                    ),
                });
            } else {
                toast({ title: `${experiment.name} enabled!` });
            }

            // Hard reload to ensure experiment changes take effect
            window.location.reload();
        }
    };

    const handleConfirmDisable = () => {
        setShowDisableDialog(false);
        setEnabled(false);

        segmentTrack("experiment_disabled", {
            experiment_name: experimentName,
            experiment_display_name: experiment.name,
            source: "settings",
        });

        toast({ title: `${experiment.name} disabled` });

        // Hard reload to ensure experiment changes take effect
        window.location.reload();
    };

    const displayState: SwitchState = isEnabled ? "checked" : "unchecked";

    return (
        <>
            <div className="flex items-center justify-between rounded-xl border border-border-base bg-surface-secondary p-4">
                <div className="flex items-center gap-3">
                    {experimentName === "ralph" ? (
                        <IconRalphWiggum size={32} className="shrink-0" />
                    ) : (
                        <FlaskConical className="size-8 shrink-0 text-content-secondary" />
                    )}
                    <div>
                        <div className="font-medium text-content-primary">{experiment.name}</div>
                        <div className="text-sm text-content-secondary">{experiment.description}</div>
                    </div>
                </div>
                <Switch
                    state={displayState}
                    onToggle={handleToggle}
                    aria-label={`Toggle ${experiment.name}`}
                    data-tracking-id={`toggle-experiment-${experimentName}`}
                />
            </div>

            <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
                <DialogContent data-track-location={TrackLocations.ExperimentsSettings}>
                    <DialogHeader>
                        <DialogTitle>Disable {experiment.name}?</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to disable {experiment.name}? You can re-enable it at any time.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="secondary">Cancel</Button>
                        </DialogClose>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDisable}
                            data-tracking-id="confirm-disable-experiment"
                        >
                            Disable
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
